// CreateTopicModal — serves BOTH create and edit ("Add topic" / "Edit topic"):
// a single required Name field, since a topic is just a title. Create POSTs
// /topic-sets/create; passing `topic` switches to edit (pencil icon, "Save
// changes"), seeding from that row and PATCHing /topic-sets/update/:id — the
// two share the `{ title }` body.
//
// The draft re-seeds on every open, so Cancel really discards; required-field
// validation runs before the request (same `submitted` idiom as
// CreateOptionSetModal).

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Field, Input, Modal } from '@/components/common';
import {
  useCreateTopicMutation,
  useUpdateTopicMutation,
  type Topic,
} from '@/api/endpoints/topicSetsApi';

interface CreateTopicModalProps {
  open: boolean;
  onClose: () => void;
  /** Present → edit that topic instead of creating a new one. */
  topic?: Topic | null;
}

const CreateTopicModal = ({ open, onClose, topic = null }: CreateTopicModalProps) => {
  const { t } = useTranslation();
  const [createTopic, { isLoading: creating }] = useCreateTopicMutation();
  const [updateTopic, { isLoading: updating }] = useUpdateTopicMutation();
  const saving = creating || updating;
  const isEdit = Boolean(topic);
  const [title, setTitle] = useState('');
  const [submitted, setSubmitted] = useState(false);

  // Re-seed each time the modal opens — from the edited row, or blank for a
  // new topic. Discards leftovers on close either way.
  useEffect(() => {
    if (!open) return;
    setTitle(topic?.title ?? '');
    setSubmitted(false);
  }, [open, topic]);

  const titleMissing = !title.trim();

  const save = async () => {
    // Validate before any backend call: surface the error, bail while invalid.
    setSubmitted(true);
    if (titleMissing) return;
    const name = title.trim();
    try {
      if (topic) {
        await updateTopic({ id: topic.id, body: { title: name } }).unwrap();
        toast.success(t('data_structure_tp_updated', { name }));
      } else {
        await createTopic({ title: name }).unwrap();
        toast.success(t('data_structure_tp_created', { name }));
      }
      onClose();
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={isEdit ? 'pencil' : 'bookmark'}
      title={t(isEdit ? 'data_structure_tp_edit_title' : 'data_structure_tp_create_title')}
      subtitle={t('data_structure_tp_create_subtitle')}
      footer={
        <>
          <Button variant='secondary' onClick={onClose} disabled={saving}>
            {t('button_cancel')}
          </Button>
          <Button iconStart={isEdit ? 'check' : 'plus'} onClick={save} loading={saving}>
            {t(isEdit ? 'button_save_changes' : 'data_structure_tp_create')}
          </Button>
        </>
      }
    >
      <Field
        label={t('data_structure_tp_name')}
        required
        error={submitted && titleMissing ? t('data_structure_tp_name_required') : undefined}
      >
        <Input
          autoFocus
          value={title}
          placeholder={t('data_structure_tp_name_ph')}
          error={submitted && titleMissing}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Field>
    </Modal>
  );
};

export default CreateTopicModal;
